import React, { FC, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { LogoTransfix } from 'transfix-ui/components/LogoTransfix'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { StringParam, useQueryParams } from 'use-query-params'
import { clearUser } from 'utils/actions/user'
import { useChatWidgetContext } from 'utils/context/ChatWidgetContext'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import {
  EmailInputForm,
  EmailInputFormSchemaType,
} from 'apps/fleet-planner/components/forms/EmailInputForm'
import { getRoute, IOnboardingQueryParams } from 'apps/fleet-planner/constants'
import { stepRoutesByName } from 'apps/fleet-planner/constants/onboarding'
import { useOnboardingWizardContext, useUserContext } from 'apps/fleet-planner/context'
import { useUserAuth, useFMSAnalytics } from 'apps/fleet-planner/hooks'
import TruckSceneImage from 'assets/truck-scene-bg.jpg'
import { TLogoutReason } from 'utils/userAuth'

export interface IOnboardingStepEmailInputProps {}

const useEmailInputStepStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    width: '100%',
    height: '100%',
  },
  formWrapper: {
    display: 'flex',
    flex: '0 0 100%',
    padding: theme.spacing(3),

    [theme.breakpoints.up('md')]: {
      alignItems: 'center',
      flex: '0 0 60%',
    },

    [theme.breakpoints.up('lg')]: {
      flex: '0 0 50%',
    },
  },
  imageWrapper: {
    flex: '0 0 100%',
    overflow: 'hidden',
    display: 'none',

    [theme.breakpoints.up('md')]: {
      backgroundSize: 'cover',
      backgroundPosition: 'bottom right',
      backgroundImage: `url(${TruckSceneImage})`,
      position: 'fixed',
      display: 'block',
      width: '40%',
      top: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
    },

    [theme.breakpoints.up('lg')]: {
      width: '50%',
      flex: '0 0 50%',
    },
  },
  form: {
    flex: '0 0 100%',

    [theme.breakpoints.up('md')]: {
      maxWidth: 424,
      margin: '0 auto',

      '@media screen and (min-height:800px)': {
        marginTop: `-${theme.spacing(10)}`,
      },
    },
  },
}))

export const OnboardingStepEmailInput: FC<IOnboardingStepEmailInputProps> = () => {
  const { logout } = useUserDetails()
  const { clearUserData, userData } = useUserContext()
  const isFmsRedirectUsersAdminApprovalEnabled = useFeatureFlag('fmsRedirectUsersAdminApproval')
  const { trackEvent } = useFMSAnalytics()
  const history = useHistory()
  const { requestLink } = useUserAuth()
  const { mobileApp, setUserEmail, carriers = [] } = useOnboardingWizardContext()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [isLoading, setLoading] = useState<boolean>(false)
  const userId = userData?.user?.id ?? undefined
  const noCarriers = !carriers.length
  const shouldRouteToUnapprovedCarriers =
    noCarriers && userId && isFmsRedirectUsersAdminApprovalEnabled
  /**
   * This useEffect route unapproved users that don't have a motor_carrier to the unapproved carrier page
   */
  useEffect(() => {
    if (shouldRouteToUnapprovedCarriers) {
      history.push(stepRoutesByName.unapprovedCarriers)
    }
  }, [history, shouldRouteToUnapprovedCarriers])

  useEffect(() => {
    trackEvent('Onboarding', 'ONBOARDING_VIEWED_SIGNUP_PAGE')
  }, [trackEvent])

  /**
   * Triggers API method to send a link to the user's email
   */
  const submitHandler = async ({ email = '' }: EmailInputFormSchemaType) => {
    if (!email || isLoading) return
    setLoading(true)
    try {
      await requestLink(email)
      setUserEmail(email)
      setLoading(false)
      trackEvent('Onboarding', 'ONBOARDING_SUCCESSFULLY_SUBMITTED_FORM_ON_SIGNUP_PAGE', { email })
      const params: IOnboardingQueryParams = {}
      if (mobileApp) {
        params.mobileApp = 1
      }
      history.push(getRoute('checkEmail', params))
    } catch (e) {
      trackEvent('Onboarding', 'ONBOARDING_FORM_API_ERROR_SIGNUP_PAGE', { e })
      setErrorMessage(e)
      setLoading(false)
    }
  }

  const dispatch = useDispatch()
  const { shutdown } = useChatWidgetContext()

  const logoutHandler = async () => {
    trackEvent('Login', 'LOGGED_OUT')
    dispatch(clearUser)

    await clearUserData()

    shutdown(true)

    logout({ origin: 'fms' })
  }

  const classes = useEmailInputStepStyles()

  const [{ logoutReason }] = useQueryParams({
    logoutReason: StringParam,
  })

  const { closeToast, openToast, toastMessage, toastOpen } = useToastState()
  // set the toast value on mount/queryParamChange
  useEffect(() => {
    const accountDeletionSuccess = (logoutReason as TLogoutReason) === 'accountDeletion'
    if (accountDeletionSuccess) {
      openToast({
        toastMessage: 'Your account has been successfully deleted.',
        toastType: 'default',
      })
    }
  }, [logoutReason])

  return (
    <Box className={classes.root}>
      <Box className={classes.formWrapper}>
        <Box className={classes.form}>
          {!mobileApp && (
            <Box mb={3}>
              <LogoTransfix color='black' />
            </Box>
          )}
          <Box mb={3}>
            <Typography variant='h2'>Grow and manage your freight business</Typography>
          </Box>
          <Box mb={3}>
            <Typography variant='subtitle1' component='div'>
              Keep your trucks loaded and organize your team, all in one place.
            </Typography>
          </Box>
          <Box mb={4}>
            <ul>
              <li>
                <Typography variant='body2' component='div'>
                  Book loads instantly at the best rates
                </Typography>
              </li>
              <li>
                <Typography variant='body2' component='div'>
                  Manage and plan your driver&apos;s schedules with our free, complimentary fleet
                  planner suite
                </Typography>
              </li>
              <li>
                <Typography variant='body2' component='div'>
                  Personalize your load offers based on what works best for your team
                </Typography>
              </li>
            </ul>
          </Box>
          <EmailInputForm onSuccess={submitHandler} error={errorMessage} isLoading={isLoading} />
          {!mobileApp && (
            <Box mt={2} textAlign='center'>
              Already have an account?{' '}
              <button className='btn btn-text-link' onClick={logoutHandler}>
                Log In
              </button>
            </Box>
          )}
        </Box>
      </Box>
      <Box className={classes.imageWrapper} />
      <Toast duration={null} message={toastMessage} open={toastOpen} onClose={closeToast} />
    </Box>
  )
}
